/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: QUICK REACTION (REACCIÓN RÁPIDA 1 VS 1)
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub para recibir telemetría autoritativa,
 * conteo regresivo sincronizado, ventana de retardo aleatorio, señal de disparo, faltas tempranas y proclamación de ganadores.
 * 
 * @module utilities/WS/Admin/QuickReactionWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Reacción Rápida ('quickreaction:admin').
 * 
 * Eventos clave recibidos:
 * - 'quick_reaction_state': Estado completo en memoria.
 * - 'quick_reaction_countdown_tick': Tick de conteo regresivo (3, 2, 1).
 * - 'quick_reaction_random_wait': Ventana de suspenso / espera aleatoria.
 * - 'quick_reaction_trigger_active': Señal oficial de disparo con timestamp UTC del servidor.
 * - 'quick_reaction_early_fault': Notificación de falta por disparo anticipado.
 * - 'quick_reaction_match_finished': Proclamación del ganador y tiempos de reacción finales.
 * - 'quick_reaction_player_joined': Notificación de nuevo jugador conectado a un slot.
 * - 'quick_reaction_player_left': Notificación de desconexión o desvinculación de un participante.
 * - 'quick_reaction_cover_shown': Portada proyectada en sala.
 * - 'quick_reaction_game_scene_shown' / 'quick_reaction_game_shown': Escena de combate proyectada.
 * - 'quick_reaction_reset' / 'quick_reaction_game_ready': Sala reiniciada a estado listo para revancha.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar mensajes y eventos emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 */
export const ConnectWebSocketForQuickReactionAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = ''
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'quickreaction:admin',
        roomId: roomId || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[QuickReactionAdminWS] Error al establecer conexión con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Reacción Rápida y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForQuickReactionAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[QuickReactionAdminWS] Error al desconectar la sesión de SignalR del operador:', disconnectionError);
    }
};

/**
 * Envía un mensaje o comando desde el panel de administración a través de la conexión activa.
 * 
 * @param {Object|string} payload - Mensaje o comando a transmitir.
 * @returns {boolean} True si se envió correctamente.
 */
export const SendAdminMessage = (payload) => {
    try {
        return SignalRService.send(payload);
    } catch (e) {
        return false;
    }
};

export const SendQuickReactionAdminMessage = SendAdminMessage;

export default {
    ConnectWebSocketForQuickReactionAdmin,
    DisconnectWebSocketForQuickReactionAdmin,
    SendAdminMessage,
    SendQuickReactionAdminMessage
};

